import React, { useEffect, useState } from 'react'
import './App.css'
import './KScannerPage.css'
import { Link } from 'react-router-dom'
import Header from './layouts/Header'

// ---- EDIT THESE ----
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.karamalescanner&hl=en'
const APP_META = 'Free on Google Play · Android'
// --------------------

const img = (name) => `${process.env.REACT_APP_API_URL}/apps/kscanner/karamale_scanner/${name}.webp`

const PRINCIPLES = [
  ['Scan fast', 'Point your camera at a ticket. No typing, no hassle, even at busy entrances.'],
  ['Spot fakes', 'Counterfeit and invalid tickets are flagged automatically.'],
  ['Stay in sync', 'Push and pull keeps every scanner on your team consistent.'],
]

const STEPS = [
  ['ob-scan', 'Scan tickets in seconds'],
  ['ob-fakes', 'Spot fakes instantly'],
  ['ob-gates', 'Built for busy gates'],
  ['login', 'Log in and go'],
]

const MODES = [
  ['Local', 'Works offline', 'Use it on one device, with no connection needed at the gate.'],
  ['Live', 'Real-time', 'Multiple devices scanning the same event, kept in step as you go.', true],
]

const DASH_POINTS = [
  'Progress bar and scanned-vs-sold count',
  'Physical and digital tickets tracked separately',
  'Sync controls right on the event screen',
]

const SYNC_POINTS = [
  'Global Sync covers all your events',
  'Selective Sync targets just one event, lighter on data',
  'Clear confirmation of what was reconciled',
]

const FAQS = [
  {
    id: 'offline',
    question: 'Does Kscanner work without internet?',
    answer:
      'Yes. Local mode works offline on a single device. Live mode syncs in real time across multiple devices and needs a connection.',
  },
  {
    id: 'logout',
    question: 'What should I do before logging out?',
    answer:
      'Push your scans to the server first. Open the event, tap Push under Sync and wait for it to finish. Logging out before you push means unsynced scans are lost.',
  },
  {
    id: 'restore',
    question: 'How do I get my data back after logging in?',
    answer:
      'Log in, open the event and tap Pull under Sync. This downloads all scanned data to your device, including scans uploaded from other devices.',
  },
  {
    id: 'install',
    question: 'Where can I download Kscanner?',
    answer:
      'Kscanner is available on Google Play. Install it like any other app, then log in with your Karamale account.',
  },
]

const Phone = ({ name, alt, tilt }) => (
  <figure className={`kx-ks-phone ${tilt ? `kx-ks-phone--${tilt}` : ''}`}>
    <img src={img(name)} alt={alt} loading="lazy" />
  </figure>
)

const DownloadButton = ({ variant = 'brand', children = 'Get it on Google Play' }) => (
  <a
    href={PLAY_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`kx-ks-btn kx-ks-btn--${variant}`}
  >
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M6 3.5v17l14-8.5z" fill="currentColor" />
    </svg>
    {children}
  </a>
)

const Eyebrow = ({ children }) => (
  <span className="kx-ks-pill">
    <i /> {children}
  </span>
)

const ModeCard = ({ label, value, description, featured }) => (
  <article className={`kx-ks-fee ${featured ? 'kx-ks-fee--hot' : ''}`}>
    <div className="kx-ks-fee__top">
      <span>{label}</span>
      {featured && <b>Multi-device</b>}
    </div>
    <strong>{value}</strong>
    <p>{description}</p>
  </article>
)

const Checks = ({ title, items }) => (
  <div className="kx-ks-panel">
    <div className="kx-ks-panel__head">
      <span>{title}</span>
      <b>
        <i /> Live
      </b>
    </div>
    <ul className="kx-ks-checks">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  </div>
)

const Row = ({ reverse, eyebrow, title, accent, children, visual }) => (
  <section className="kx-ks-section">
    <div className={`kx-ks-wrap kx-ks-two ${reverse ? 'kx-ks-two--reverse' : ''}`}>
      <div className="kx-ks-two__copy">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="kx-ks-h2 kx-ks-h2--mt">
          {title} <span>{accent}</span>
        </h2>
        {children}
      </div>
      <div className="kx-ks-two__visual">{visual}</div>
    </div>
  </section>
)

const KScannerPage = () => {
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    document.title = 'Kscanner - Ticket scanning app by Karamale'
  }, [])

  return (
    <div className="kx-ks-page">
      <Header />

      {/* HERO */}
      <section className="kx-ks-hero">
        <div className="kx-ks-aurora" aria-hidden="true">
          <span className="kx-ks-aurora__a" />
          <span className="kx-ks-aurora__b" />
        </div>
        <div className="kx-ks-wrap kx-ks-hero__inner">
          <div>
            <Eyebrow>Kscanner by Karamale</Eyebrow>
            <h1>
              Every ticket checked
              <br />
              <em>in seconds.</em>
            </h1>
            <p className="kx-ks-hero__sub">
              The secure scanning app for your event entrance. Point, scan, and let Kscanner flag
              fake and invalid tickets instantly.
            </p>
            <div className="kx-ks-actions">
              <DownloadButton />
              <a href="#screens" className="kx-ks-btn kx-ks-btn--ghost">
                See the app
              </a>
            </div>
            <small className="kx-ks-hero__meta">{APP_META}</small>
          </div>

          <div className="kx-ks-hero__visual">
            <div className="kx-ks-hero__phones">
              <Phone name="dashboard" alt="Kscanner check-in dashboard" tilt="left" />
              <Phone name="welcome" alt="Kscanner welcome screen" tilt="right" />
            </div>
            <div className="kx-ks-mini kx-ks-mini--one">
              <small>Counterfeit tickets</small>
              <strong>Flagged</strong>
              <span>instantly, at the gate</span>
            </div>
            <div className="kx-ks-mini kx-ks-mini--two">
              <small>Scan modes</small>
              <strong>Local + Live</strong>
              <span>offline or real-time</span>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="kx-ks-section">
        <div className="kx-ks-wrap">
          <h2 className="kx-ks-h2 kx-ks-h2--solo">
            Three things <span>to remember.</span>
          </h2>
          <div className="kx-ks-cols">
            {PRINCIPLES.map(([title, text], i) => (
              <div key={title} className={`kx-ks-card ${i === 2 ? 'kx-ks-card--hot' : ''}`}>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIRST LAUNCH */}
      <section className="kx-ks-section" id="screens">
        <div className="kx-ks-wrap">
          <div className="kx-ks-head">
            <div>
              <Eyebrow>First launch</Eyebrow>
              <h2 className="kx-ks-h2 kx-ks-h2--mt">
                From install to <span>first scan.</span>
              </h2>
            </div>
            <p>
              Four quick screens and you are at the gate. Install, log in and start checking in.
            </p>
          </div>
          <div className="kx-ks-strip">
            {STEPS.map(([name, label], i) => (
              <div key={name} className="kx-ks-strip__item">
                <Phone name={name} alt={label} />
                <span className="kx-ks-strip__n">{i + 1}</span>
                <strong>{label}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD */}
      <Row
        eyebrow="Check-in"
        title="Know your door"
        accent="at a glance."
        visual={<Phone name="dashboard" alt="Check-in progress and scan modes" />}
      >
        <p className="kx-ks-lead">
          Live check-in progress, physical and digital ticket counts and total sold, all on one
          screen. Start scanning in one tap.
        </p>
        <Checks title="On the event screen" items={DASH_POINTS} />
      </Row>

      {/* LOCAL VS LIVE */}
      <section className="kx-ks-section">
        <div className="kx-ks-wrap">
          <div className="kx-ks-head">
            <div>
              <Eyebrow>Start scanning</Eyebrow>
              <h2 className="kx-ks-h2 kx-ks-h2--mt">
                Local or live. <span>Your call.</span>
              </h2>
            </div>
            <p>
              Scan on a single device with no signal, or sync several scanners across the same event
              in real time.
            </p>
          </div>
          <div className="kx-ks-cols kx-ks-cols--two">
            {MODES.map(([label, value, description, featured]) => (
              <ModeCard
                key={label}
                label={label}
                value={value}
                description={description}
                featured={featured}
              />
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <Row
        reverse
        eyebrow="Your events"
        title="All your events,"
        accent="one login."
        visual={<Phone name="events" alt="Event list in Kscanner" />}
      >
        <p className="kx-ks-lead">
          Sign in with your Karamale account and your events are ready. Pick one and start checking
          people in.
        </p>
        <Link to="/organiser-registration" className="kx-ks-btn kx-ks-btn--brand">
          Create organiser account
        </Link>
      </Row>

      {/* SYNC */}
      <Row
        eyebrow="Sync"
        title="Push. Pull."
        accent="Stay coordinated."
        visual={
          <div className="kx-ks-duo">
            <Phone name="sync" alt="Global sync with Pull & Merge and Push" />
            <Phone name="sync-about" alt="About syncing explainer" />
          </div>
        }
      >
        <p className="kx-ks-lead">
          Push sends the tickets scanned on this device so the rest of your team can see them. Pull
          brings the latest scans and status changes back. Fewer duplicates, fewer invalid scans.
        </p>
        <Checks title="Sync options" items={SYNC_POINTS} />
      </Row>

      {/* LOGOUT */}
      <Row
        reverse
        eyebrow="Built-in safety net"
        title="It warns you before"
        accent="you lose a scan."
        visual={<Phone name="logout" alt="Log out warning with sync steps" />}
      >
        <p className="kx-ks-lead">
          Before logging out, Kscanner reminds you to push unsynced scans, then shows exactly how to
          get your data back when you log in again.
        </p>
        <div className="kx-ks-note">
          <span className="kx-ks-tick" aria-hidden="true" />
          <div>
            <strong>Push before you log out.</strong>
            <p>Unsynced scans are lost if you log out first.</p>
          </div>
        </div>
      </Row>

      {/* FAQ */}
      <section className="kx-ks-section">
        <div className="kx-ks-wrap kx-ks-faq">
          <div>
            <Eyebrow>Questions</Eyebrow>
            <h2 className="kx-ks-h2 kx-ks-h2--mt">
              Frequently asked <span>questions.</span>
            </h2>
          </div>
          <div className="kx-ks-faq__list">
            {FAQS.map((item) => {
              const open = openFaq === item.id
              return (
                <div key={item.id} className={`kx-ks-faq__item ${open ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenFaq((c) => (c === item.id ? null : item.id))}
                  >
                    <span>{item.question}</span>
                    <b aria-hidden="true">{open ? '−' : '+'}</b>
                  </button>
                  {open && <div className="kx-ks-faq__answer">{item.answer}</div>}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="kx-ks-section kx-ks-section--last">
        <div className="kx-ks-wrap">
          <div className="kx-ks-cta">
            <h2>
              Your next gate <em>starts here.</em>
            </h2>
            <p>Download Kscanner, log in with your Karamale account and start scanning.</p>
            <div className="kx-ks-actions kx-ks-actions--center">
              <DownloadButton variant="light" />
              <Link to="/organiser-registration" className="kx-ks-btn kx-ks-btn--outline">
                Create organiser account
              </Link>
            </div>
            <small>{APP_META}</small>
          </div>
        </div>
      </section>
    </div>
  )
}

export default KScannerPage
